import { useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useSearchParams,
} from 'react-router-dom'
import { ensureSession } from './api/auth'
import { AppLayout, RequireAdmin, RequireAuth } from './components/Layout'
import { Toaster } from './components/ui/Toaster'
import { I18nProvider } from './i18n/I18nProvider'
import { queryClient } from './lib/queryClient'
import { AdminPage } from './pages/AdminPage'
import { BillingPage } from './pages/BillingPage'
import { ClientsPage } from './pages/ClientsPage'
import { ConfirmEmailChangePage } from './pages/ConfirmEmailChangePage'
import { DeleteAccountPage } from './pages/DeleteAccountPage'
import { EntriesPage } from './pages/EntriesPage'
import { InvoicesPage } from './pages/InvoicesPage'
import { HomePage } from './pages/LandingPage'
import { LoginPage } from './pages/LoginPage'
import { AgbPage, DatenschutzPage, ImpressumPage } from './pages/LegalPages'
import { MonthlyHoursPage } from './pages/MonthlyHoursPage'
import { ReportsPage } from './pages/ReportsPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { SettingsPage } from './pages/SettingsPage'
import { TrackPage } from './pages/TrackPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import {
    AddressesTab,
    GeneralTab,
    MembersTab,
    WorkspacePage,
} from './pages/WorkspacePage'

/** Invite-Mails linken auf /signIn?inviteToken=… — Query mitnehmen. */
function SignInRedirect() {
    const [params] = useSearchParams()
    const qs = params.toString()
    return <Navigate to={qs ? `/login?${qs}` : '/login'} replace />
}

export default function App() {
    // Beim Boot still die Sitzung über den Refresh-Cookie wiederherstellen
    useEffect(() => {
        void ensureSession()
    }, [])

    return (
        <QueryClientProvider client={queryClient}>
            <I18nProvider>
                <BrowserRouter>
                <Routes>
                    <Route path='/login' element={<LoginPage />} />
                    {/* Öffentliche Token-Seiten (Ziele der Mail-Links) */}
                    <Route
                        path='/resetPassword'
                        element={<ResetPasswordPage />}
                    />
                    <Route path='/verifyEmail' element={<VerifyEmailPage />} />
                    <Route
                        path='/confirmEmailChange'
                        element={<ConfirmEmailChangePage />}
                    />
                    <Route
                        path='/deleteAccount'
                        element={<DeleteAccountPage />}
                    />
                    <Route path='/signIn' element={<SignInRedirect />} />
                    {/* Öffentliche Rechtsseiten */}
                    <Route path='/impressum' element={<ImpressumPage />} />
                    <Route path='/datenschutz' element={<DatenschutzPage />} />
                    <Route path='/agb' element={<AgbPage />} />
                    {/* Alt-URLs des Nuxt-Frontends (/at-Präfix) umleiten */}
                    <Route path='/at' element={<Navigate to='/' replace />} />
                    <Route
                        path='/at/impressum'
                        element={<Navigate to='/impressum' replace />}
                    />
                    <Route
                        path='/at/datenschutz'
                        element={<Navigate to='/datenschutz' replace />}
                    />
                    <Route
                        path='/at/agb'
                        element={<Navigate to='/agb' replace />}
                    />
                    {/* Startseite ist öffentlich (wie im Nuxt-Frontend):
                        Landing für Ausgeloggte, Eingeloggte werden vom
                        HomePage-Gate direkt zur Zeiterfassung geleitet. */}
                    <Route path='/' element={<HomePage />} />
                    <Route
                        element={
                            <RequireAuth>
                                <AppLayout />
                            </RequireAuth>
                        }
                    >
                    <Route path='/track' element={<TrackPage />} />
                    {/* Gruppe „Abrechnung“: Einträge · Berichte · Rechnungen */}
                    <Route path='/billing' element={<BillingPage />}>
                        <Route
                            index
                            element={<Navigate to='/billing/entries' replace />}
                        />
                        <Route path='entries' element={<EntriesPage />} />
                        <Route path='reports' element={<ReportsPage />} />
                        <Route path='invoices' element={<InvoicesPage />} />
                        <Route path='monthly' element={<MonthlyHoursPage />} />
                    </Route>
                    {/* Gruppe „Workspace“: Kunden · Mitglieder · Adressen · Allgemein */}
                    <Route path='/workspace' element={<WorkspacePage />}>
                        <Route
                            index
                            element={<Navigate to='/workspace/clients' replace />}
                        />
                        <Route path='clients' element={<ClientsPage />} />
                        <Route path='members' element={<MembersTab />} />
                        <Route path='addresses' element={<AddressesTab />} />
                        <Route path='general' element={<GeneralTab />} />
                    </Route>
                    <Route path='/settings' element={<SettingsPage />} />
                    <Route
                        path='/admin'
                        element={
                            <RequireAdmin>
                                <AdminPage />
                            </RequireAdmin>
                        }
                    />
                    {/* Alte Pfade → neue Gruppen-Routen (Bookmarks) */}
                    <Route
                        path='/entries'
                        element={<Navigate to='/billing/entries' replace />}
                    />
                    <Route
                        path='/reports'
                        element={<Navigate to='/billing/reports' replace />}
                    />
                    <Route
                        path='/invoices'
                        element={<Navigate to='/billing/invoices' replace />}
                    />
                    <Route
                        path='/clients'
                        element={<Navigate to='/workspace/clients' replace />}
                    />
                    </Route>
                    <Route path='*' element={<Navigate to='/' replace />} />
                </Routes>
                    <Toaster />
                </BrowserRouter>
            </I18nProvider>
        </QueryClientProvider>
    )
}
